import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import ExternalLinkIcon from '../../ExternalLinkIcon';
import { useRestaurantSettings } from '../../../hooks/useRestaurantSettings';
import { useSiteLogo } from '../../../hooks/useSiteLogo';
import { formatTelHref } from '../../../helpers/phone';
import { formatCoordinates } from '../../../helpers/geo';
import type { MarkerData } from './pins';
import './location.scss';

const LeafletMap = lazy(() => import('./LeafletMap'));

const defaultDistances = [
  { city: 'Bruxelles', time: '35 min' },
  { city: 'Namur', time: '30 min' },
  { city: 'Liège', time: '55 min' },
];

const View = ({ data, isEditMode }: any) => {
  const settings = useRestaurantSettings();
  const mapRef = useRef<any>(null);
  // The server cannot render the map. The first render in the browser must
  // give the same HTML as the server, so the map comes after the mount.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const siteLogo = useSiteLogo();

  const handleMap = useCallback((map: any) => {
    mapRef.current = map;
  }, []);

  const eyebrow = data.eyebrow || 'Nous trouver';
  const heading =
    data.heading || 'À cinq minutes de la Grand-Place de Jodoigne.';
  const distances =
    data.distances?.length > 0 ? data.distances : defaultDistances;

  const markers: MarkerData[] =
    data.markers?.length > 0
      ? data.markers
          .filter((m: any) => m.lat && m.lng)
          .map((m: any) => ({
            lat: Number(m.lat),
            lng: Number(m.lng),
            label: m.label || undefined,
            type: m.type || 'restaurant',
          }))
      : [
          {
            lat: settings.latitude,
            lng: settings.longitude,
            label: `À ciel ouvert — ${settings.address_line1}, ${settings.postal_code} ${settings.city}`,
            type: 'restaurant' as const,
          },
        ];

  // The coordinates text follows the restaurant marker, so it cannot drift.
  const restaurant =
    markers.find((marker) => marker.type === 'restaurant') || markers[0];
  const coordinates = restaurant
    ? formatCoordinates(restaurant.lat, restaurant.lng)
    : '';

  const fullWidth = data.full_width ?? false;
  // The map height set in the sidebar. On wide screens it is a minimum, so
  // the map can grow with the card that sits on it.
  const mapStyle = data.map_height
    ? ({ '--map-h': `${data.map_height}px` } as React.CSSProperties)
    : undefined;

  const cls = `find${fullWidth ? ' find--full' : ''}`;

  const stage = (
    <div className="find-stage">
      <div className="find-map" style={mapStyle}>
        {mounted && markers.length > 0 ? (
          <Suspense
            fallback={
              <div className="find-map-placeholder">
                <span>Chargement de la carte…</span>
              </div>
            }
          >
            <LeafletMap
              markers={markers}
              onMap={handleMap}
              apiKey={settings.carto_api_key}
            />
          </Suspense>
        ) : (
          <div className="find-map-placeholder">
            <span>Carte interactive</span>
          </div>
        )}
        <div className="find-zoom">
          <button
            type="button"
            className="find-zoom-btn"
            aria-label="Zoom arrière"
            onClick={() => mapRef.current?.zoomOut()}
          >
            −
          </button>
          <button
            type="button"
            className="find-zoom-btn"
            aria-label="Zoom avant"
            onClick={() => mapRef.current?.zoomIn()}
          >
            +
          </button>
        </div>
      </div>
      <aside className="find-card">
        {siteLogo && <img className="find-card-logo" src={siteLogo} alt="" />}
        <h3>{settings.address_line1}</h3>
        <p className="find-address">
          {settings.postal_code} {settings.city}
          <br />
          {settings.country}
        </p>
        <dl style={{ margin: 0 }}>
          <div className="find-row">
            <dt>Fixe</dt>
            <dd>
              <a href={formatTelHref(settings.phone_primary)}>
                {settings.phone_primary}
              </a>
            </dd>
          </div>
          {settings.phone_secondary && (
            <div className="find-row">
              <dt>Mobile</dt>
              <dd>
                <a href={formatTelHref(settings.phone_secondary)}>
                  {settings.phone_secondary}
                </a>
              </dd>
            </div>
          )}
          {settings.parking_info && (
            <div className="find-row">
              <dt>Parking</dt>
              <dd>{settings.parking_info}</dd>
            </div>
          )}
          {settings.access_info && (
            <div className="find-row">
              <dt>Accès</dt>
              <dd>{settings.access_info}</dd>
            </div>
          )}
        </dl>
        <div className="find-card-footer">
          {settings.maps_url && (
            <a
              href={settings.maps_url}
              className="btn btn-primary external-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Itinéraire
              <ExternalLinkIcon />
            </a>
          )}
        </div>
      </aside>
    </div>
  );

  // The full-width variant renders the stage outside the shell so the map
  // spans the section without viewport-unit tricks.
  return (
    <section className={cls} id="nous-trouver">
      <div className="shell">
        <div className="find-head">
          <p className="eyebrow">{eyebrow}</p>
          <h2>{heading}</h2>
          <p className="find-coords">
            {coordinates && <span className="geo">{coordinates}</span>}
            {distances.map((d: any, i: number) => (
              <span key={i} className="find-coord-pair">
                <span className="sep" aria-hidden="true">
                  ·
                </span>
                <span className="from">
                  <b>{d.city}</b> {d.time}
                </span>
              </span>
            ))}
          </p>
        </div>
      </div>
      {fullWidth ? stage : <div className="shell">{stage}</div>}
    </section>
  );
};

export default View;
